import {
  useFormErrorNotification,
  useFormSuccessNotification,
} from "@babackapp/react-hook-forms-components/form-notifications";
import useAllViewerShops from "@babackapp/private-api-client/client/use-all-viewer-shops";
import useDuplicateShopSettings from "@babackapp/private-api-client/client/use-duplicate-shop-settings";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Label } from "@babackapp/ui/components/label";
import { LoadingButton } from "@babackapp/ui/components/loading-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { CopyIcon } from "@radix-ui/react-icons";
import { useState } from "react";

interface Props {
  sourceShopId: string;
  sourceShopName: string;
  sourcePlanReturnToStore?: boolean | null;
}

interface SelectionState {
  carriers: boolean;
  rules: boolean;
  preferences: boolean;
  users: boolean;
  exchange: boolean;
  discount: boolean;
  reasons: boolean;
  disapprovalReasons: boolean;
  integrations: boolean;
  returnToStore: boolean;
}

type Step = "select" | "confirm";

const BASE_CATEGORIES: { key: keyof SelectionState; label: string }[] = [
  { key: "carriers", label: "Transporteurs (avec credentials)" },
  { key: "rules", label: "Règles d'éligibilité" },
  { key: "preferences", label: "Paramètres de la boutique" },
  { key: "exchange", label: "Configuration des échanges" },
  { key: "discount", label: "Configuration des avoirs / remises" },
  { key: "reasons", label: "Raisons de retour" },
  { key: "disapprovalReasons", label: "Motifs de non-approbation" },
  { key: "integrations", label: "Intégrations" },
  { key: "users", label: "Utilisateurs (invitation)" },
];

const RETURN_TO_STORE_CATEGORY: { key: keyof SelectionState; label: string } =
  { key: "returnToStore", label: "Configuration retour en boutique" };

const DEFAULT_SELECTION: SelectionState = {
  carriers: true,
  rules: true,
  preferences: true,
  users: false,
  exchange: false,
  discount: false,
  reasons: false,
  disapprovalReasons: false,
  integrations: false,
  returnToStore: false,
};

export default function DuplicateShopSettingsDialog({
  sourceShopId,
  sourceShopName,
  sourcePlanReturnToStore,
}: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("select");
  const [targetShopId, setTargetShopId] = useState<string>("");
  const [selection, setSelection] = useState<SelectionState>(DEFAULT_SELECTION);

  const categories = sourcePlanReturnToStore
    ? [...BASE_CATEGORIES, RETURN_TO_STORE_CATEGORY]
    : BASE_CATEGORIES;

  const { data: shops } = useAllViewerShops();
  const { mutateAsync: duplicateSettings, isPending } =
    useDuplicateShopSettings(sourceShopId);

  const successNotification = useFormSuccessNotification();
  const errorNotification = useFormErrorNotification();

  const targetShops =
    shops?.filter((s) => s.id !== sourceShopId) ?? [];

  const targetShop = targetShops.find((s) => s.id === targetShopId);

  const selectedCategories = categories.filter((c) => selection[c.key]);

  const canProceed =
    targetShopId !== "" && selectedCategories.length > 0;

  const handleReset = () => {
    setStep("select");
    setTargetShopId("");
    setSelection(DEFAULT_SELECTION);
  };

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) handleReset();
  };

  const handleConfirm = async () => {
    try {
      await duplicateSettings({
        targetShopId,
        carriers: selection.carriers || undefined,
        rules: selection.rules || undefined,
        preferences: selection.preferences || undefined,
        users: selection.users || undefined,
        exchange: selection.exchange || undefined,
        discount: selection.discount || undefined,
        reasons: selection.reasons || undefined,
        disapprovalReasons: selection.disapprovalReasons || undefined,
        integrations: selection.integrations || undefined,
        returnToStore: selection.returnToStore || undefined,
      });
      successNotification();
      setOpen(false);
      handleReset();
    } catch {
      errorNotification();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CopyIcon className="h-4 w-4" />
          Dupliquer ces paramètres sur une autre boutique
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-md">
        {step === "select" ? (
          <>
            <DialogHeader>
              <DialogTitle>Dupliquer les paramètres</DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-2">
              <div className="space-y-2">
                <Label>Boutique cible</Label>
                <Select value={targetShopId} onValueChange={setTargetShopId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une boutique..." />
                  </SelectTrigger>
                  <SelectContent>
                    {targetShops.map((shop) => (
                      <SelectItem key={shop.id} value={shop.id}>
                        {shop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Que souhaitez-vous copier ?</Label>
                {categories.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-3">
                    <Checkbox
                      id={`dup-${key}`}
                      checked={selection[key]}
                      onCheckedChange={(checked) =>
                        setSelection((prev) => ({
                          ...prev,
                          [key]: checked === true,
                        }))
                      }
                    />
                    <label
                      htmlFor={`dup-${key}`}
                      className="text-sm cursor-pointer"
                    >
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Annuler
              </Button>
              <Button
                disabled={!canProceed}
                onClick={() => setStep("confirm")}
              >
                Continuer
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirmer la duplication</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 text-sm">
              <p>
                Les paramètres suivants seront{" "}
                <span className="font-semibold text-destructive">
                  écrasés
                </span>{" "}
                sur{" "}
                <span className="font-semibold">{targetShop?.name}</span> :
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {selectedCategories.map((c) => (
                  <li key={c.key}>{c.label}</li>
                ))}
              </ul>
              <p className="text-muted-foreground">
                Source : <span className="font-medium">{sourceShopName}</span>
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("select")}>
                Retour
              </Button>
              <LoadingButton
                isLoading={isPending}
                variant="destructive"
                onClick={handleConfirm}
              >
                Appliquer
              </LoadingButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
